import {
  FileUpload,
  Visibility,
  CheckCircle,
  Stop,
  AccessTime,
  MoreVert,
} from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Typography,
  styled,
  useTheme,
  Box,
} from "@mui/material";
import iconFlat from "../assets/IconFlat.svg";
import iconPharmacy from "../assets/iconPharmacy.svg";

import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FormSwitch } from "../../../components/form/FormSwitch";

import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useSessionContext } from "../../../contexts/SessionContext";
import { EHRPosterPatchStatus } from "../../../services/fhir/patientsummary/EHRPosterPatchStatus";
import { EHRPosterPatchField } from "../../../services/fhir/patientsummary/EHRPosterPatchField";
import { CreateMedicationTask } from "./CreateMedicationTask";
import { IframeDialog } from "../../ehrrecords/components/IframeDialog";
import { ConfirmDocumentDialog } from "../../examsolicitation/components/ConfirmDocumentDialog";
import { StopMedicationDialog } from "./StopMedicationDialog";
import { oids } from "@/configs/Settings";

interface IProps {
  type: "medication" | "examSolicitation";
  fullData?: any;
  name?: string;
  date?: any;
  control?: any;
  result?: any;
  setRefetch?: any;
  variant?: "default" | "stack";
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export function HealthMedicineDetails({
  type,
  name,
  date,
  fullData,
  result,
  setRefetch,
  variant = "default",
}: IProps) {
  const methods = useForm({});
  const { control } = methods;
  const { user } = useSessionContext();

  const [fileBase64, setFileBase64] = useState<any>();
  const [openResult, setOpenResult] = useState<boolean>();
  const [openControl, setOpenControl] = useState<boolean>(false);
  const [renderButton, setRenderButton] = useState<boolean>(true);

  const convertToBase64 = (file: File) => {
    if (file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.readAsDataURL(file);

        fileReader.onload = () => {
          resolve(fileReader.result);
        };

        fileReader.onerror = (error) => {
          reject(error);
        };
      });
    }
  };

  const uploadImage = async (e: any) => {
    const file = e.target.files[0];
    const base64 = await convertToBase64(file);
    setFileBase64(base64);
  };

  const choiseChange = useWatch({ name: "medicationControl", control });

  function onCloseDialog() {
    setFileBase64(undefined);
  }

  const docReferenceData = {
    servicePratice: {
      code: fullData?.practiceSetting?.coding?.[0]?.code,
      system: fullData?.practiceSetting?.coding?.[0]?.system,
      display: fullData?.practiceSetting?.coding?.[0]?.display,
    },
    time: {
      start: dayjs().toISOString(),
      end: dayjs().toISOString(),
    },
  };

  const refs = {
    id: fullData?.context
      ?.find((el: any) => el?.reference?.includes("Encounter"))
      ?.reference?.split("/")?.[1],
    subject: user?.username,
    appointmentId: fullData?.context
      ?.find((el: any) => el?.reference?.includes("Appointment"))
      ?.reference?.split("/")?.[1],
  };

  // Armazena o valor anterior de choiseChange para comparação
  const [prevChoiseChange, setPrevChoiseChange] = useState<boolean | undefined>(
    undefined
  );
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  // Função para atualizar o status do medicamento e criar a tarefa
  const updateMedicationStatus = async (status: string, note?: string) => {
    try {
      // Atualiza o status do medicamento
      await EHRPosterPatchStatus({
        resourceType: "MedicationRequest",
        resourceId: fullData?.id,
        status: status,
      });
      toast.success("Alterado com sucesso");

      // Se tiver nota, atualiza o campo note do medicamento
      if (note) {
        try {
          // Verifica se já existe uma nota com authorReference que inclui 'Patient'
          // Isso permite preservar notas de profissionais (PractitionerRole/Practitioner) e
          // apenas gerenciar a nota do paciente
          const existingNotes = fullData?.note || [];
          const patientNoteIndex = existingNotes.findIndex((noteItem: any) =>
            noteItem?.authorReference?.reference?.includes('Patient')
          );

          // Prepara a nova nota com authorReference correto
          // A referência do paciente é construída usando o padrão: Patient/{{oids.cpf}}-{{cpf}}
          const newNote = {
            authorReference: {
              reference: `Patient/${oids.cpf}-${user?.username}`,
            },
            text: note
          };

          if (patientNoteIndex !== -1) {
            // Se existe uma nota do paciente, substitui apenas ela
            // Isso evita duplicação de notas do mesmo paciente
            await EHRPosterPatchField({
              resourceType: "MedicationRequest",
              resourceId: fullData?.id,
              op: "replace",
              path: `/note/${patientNoteIndex}`,
              value: newNote,
            });
          } else {
            // Se não existe nota do paciente, verifica se o array note existe
            if (Array.isArray(fullData?.note) && fullData?.note?.length > 0) {
              // Se o array note existe e tem elementos, adiciona ao final
              // Isso preserva todas as notas existentes (incluindo do profissional)
              await EHRPosterPatchField({
                resourceType: "MedicationRequest",
                resourceId: fullData?.id,
                op: "add",
                path: "/note/-",
                value: newNote,
              });
            } else {
              // Se o array note não existe ou está vazio, cria o array com a nova nota
              // Usa path "/note" para criar o array e value como array [newNote]
              await EHRPosterPatchField({
                resourceType: "MedicationRequest",
                resourceId: fullData?.id,
                op: "add",
                path: "/note",
                value: [newNote],
              });
            }
          }
        } catch (noteError) {
          console.error("Erro ao atualizar nota:", noteError);
        }
      }

      // Atualiza o objeto fullData com o novo status e nota para a criação da tarefa
      const updatedFullData = {
        ...fullData,
        status: status,
        note: note ? [{ text: note }] : fullData?.note,
      };

      // Cria uma tarefa quando o status do medicamento muda
      try {
        await CreateMedicationTask(updatedFullData, user?.name || "");
        toast.success("Tarefa criada com sucesso");
      } catch (taskError) {
        console.error("Erro ao criar tarefa:", taskError);
        // Não exibimos erro de criação de tarefa para não confundir o usuário
      }

      setRefetch((prev: any) => !prev);
    } catch (err) {
      console.log("err", err);
    }
  };

  useEffect(() => {
    // Ignorar a primeira execução (carregamento inicial)
    if (initialLoad) {
      setPrevChoiseChange(choiseChange);
      setInitialLoad(false);
      return;
    }

    // Verificar se houve realmente uma mudança no status
    if (choiseChange === prevChoiseChange) {
      return;
    }

    // Se o usuário ativou o medicamento (começou a tomar)
    if (choiseChange === true) {
      updateMedicationStatus("active");
    }
    // Se o usuário desativou o medicamento (parou de tomar)
    else if (choiseChange === false) {
      // Abre o diálogo de confirmação para escolher o motivo
      setOpenControl(true);
      // Não atualizamos o status aqui - isso será feito pelo diálogo StopMedicationDialog
    }

    // Atualiza o valor anterior
    setPrevChoiseChange(choiseChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choiseChange]);

  const theme = useTheme();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return {
          color: theme.palette.success.main,
          icon: <CheckCircle fontSize="small" />,
          text: "Ativo",
        };
      case "stopped":
        return {
          color: theme.palette.error.main,
          icon: <Stop fontSize="small" color="error" />,
          text: "Interrompido",
        };
      case "completed":
        return {
          color: theme.palette.success.main,
          icon: <CheckCircle fontSize="small" color="success" />,
          text: "Concluído",
        };
      default:
        return {
          color: theme.palette.warning.main,
          icon: <AccessTime fontSize="small" />,
          text: "Pendente",
        };
    }
  };

  const statusInfo = getStatusColor(fullData?.status || "unknown");
  const isStack = variant === "stack";

  const actionContent =
    type === "medication" ? (
      <FormSwitch
        name="medicationControl"
        label="Estou tomando"
        control={control}
        defaultValue={fullData.status === "active"}
      />
    ) : (
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        {renderButton && !result && (
          <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={<FileUpload />}
            onChange={(e) => uploadImage(e)}
          >
            Carregar Exame
            <VisuallyHiddenInput type="file" />
          </Button>
        )}
        {renderButton && result && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Visibility />}
            onClick={() => setOpenResult(true)}
          >
            Ver Resultado
          </Button>
        )}
      </Stack>
    );

  return (
    <>
      {isStack ? (
        <Stack
          spacing={0.75}
          width="100%"
          sx={{
            pt: 0.75,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          {actionContent}
        </Stack>
      ) : (
        <Card
          sx={{
            width: "100%",
            mb: 2,
            borderRadius: 2,
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
            position: "relative",
            overflow: "visible",
          }}
        >
          <CardContent>
            <Stack spacing={1}>
              <Typography variant="h6">{name}</Typography>

              <Typography variant="body2" color="text.secondary">
                {type === "medication" ? "Prescrito" : "Solicitado"} em {date}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  icon={statusInfo.icon}
                  label={statusInfo.text}
                  size="small"
                  sx={{
                    color: statusInfo.color,
                    backgroundColor: `${statusInfo.color}15`,
                  }}
                />
              </Stack>

              {actionContent}
            </Stack>
          </CardContent>
        </Card>
      )}

      {openResult && (
        <IframeDialog
          isOpen={openResult}
          handleClose={() => setOpenResult(false)}
          data={result?.resource?.content?.[0]?.attachment?.url}
        />
      )}

      {fileBase64 && (
        <ConfirmDocumentDialog
          setRenderButton={setRenderButton}
          setRefetch={setRefetch}
          fileBase64={fileBase64}
          isOpenDialog={fileBase64}
          onCloseDialog={() => onCloseDialog()}
          basedOn={fullData?.basedOn?.filter((el: any) =>
            el.reference?.includes("ServiceRequest")
          )}
          encounterReference={refs}
          data={{
            ...docReferenceData,
            base64: fileBase64?.split(",")?.[1],
            codeValue: "RESULT",
            codeDisplay: "Resultado de exame",
            categoryValue: "resultado",
            categoryDisplay: "Resultado de exame",
          }}
        />
      )}

      {/* @ts-ignore - Propriedade updateStatus foi adicionada na interface IProps */}
      <StopMedicationDialog
        isOpen={openControl}
        handleClose={() => {
          setOpenControl(false);
          setRefetch((prev: any) => !prev);
        }}
        data={fullData}
        updateStatus={updateMedicationStatus}
      />
    </>
  );
}
